import React, { useEffect, useRef, useState } from "react"
import { Link, Outlet, ScrollRestoration } from "react-router-dom" // Only import Link and Outlet
import logo from "./assets/logo.webp"

function App() {
  const [scrolled, setScrolled] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true)
      } else {
        setScrolled(false)
      }

      // Close menu on scroll
      if (isMenuOpen) {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [isMenuOpen])

  // Handle clicks outside the menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false) // Close the menu if clicked outside
      }
    }

    // Add event listener for clicks
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      // Clean up the event listener
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isMenuOpen])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }
  const [openFaqIndex, setOpenFaqIndex] = useState<number | undefined>(
    undefined
  )

  const faqs = [
    {
      question: "How long does it take to build a modular home?",
      answer:
        "On average, it takes 3-6 months from design to completion, depending on the complexity of the project.",
    },
    {
      question: "Can I customize my modular home?",
      answer:
        "Yes! We offer a wide range of customization options to make your home uniquely yours.",
    },
    {
      question: "Are modular homes energy-efficient?",
      answer:
        "Absolutely! Our modular homes are designed with energy-efficient materials and systems to reduce your carbon footprint and utility bills.",
    },
    {
      question: "Can I finance a modular home?",
      answer:
        "Yes, we offer financing options to help you build your dream home within your budget.",
    },
    {
      question: "What is the lifespan of a modular home?",
      answer:
        "Modular homes are built to last as long as traditional homes, often exceeding 50 years with proper maintenance.",
    },
    {
      question: "Do modular homes appreciate in value?",
      answer:
        "Yes, modular homes appreciate in value just like traditional homes, especially when well-maintained and upgraded.",
    },
    {
      question: "Can I add onto my modular home later?",
      answer:
        "Yes, modular homes can be expanded in the future to accommodate your growing needs.",
    },
    {
      question: "Are modular homes safe in extreme weather?",
      answer:
        "Yes, modular homes are built to meet or exceed local building codes, making them safe and durable in extreme weather conditions.",
    },
    {
      question: "What is included in the base price of a modular home?",
      answer:
        "The base price includes the structure, basic finishes, and essential systems. Customizations and upgrades are additional.",
    },
  ]
  const toggleFaq = (index?: number) => {
    setOpenFaqIndex(openFaqIndex === index ? undefined : index)
  }
  return (
    <div>
      <ScrollRestoration />
      <div className="bg-gray-50 min-h-screen font-sans">
        <header>
          <div
            className={`w-full px-6 py-2 flex items-center justify-between fixed z-10 opacity-95 transition-colors duration-1000 ease-in-out ${
              scrolled ? "bg-gray-50 shadow" : "bg-transparent"
            }`}
          >
            {/* Logo */}
            <Link to="/">
              <img
                src={logo}
                alt="logo"
                loading="lazy"
                className={`object-cover transition-all duration-700 ease-in-out transform ${
                  scrolled ? "w-18 h-12 scale-80" : "w-30 h-20 scale-100"
                }`}
              />
            </Link>

            {/* Hamburger Menu (Mobile) */}
            <div
              onClick={toggleMenu}
              className={`lg:hidden text-gray-600 focus:outline-none cursor-pointer`}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16m-7 6h7"
                />
              </svg>
            </div>

            {/* Navigation Links */}
            <nav
              ref={menuRef}
              className={`lg:flex lg:items-center lg:space-x-8 ${
                isMenuOpen
                  ? `block absolute transition-all duration-700 ease-in-out transform ${
                      scrolled ? "top-16" : "top-24"
                    } left-0 w-full bg-gray-50 shadow-lg`
                  : "hidden"
              }`}
            >
              <ul className="text-base xl:text-xl lg:text-xl md:text-xl flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-8 p-4 lg:p-0">
                <li>
                  <Link
                    to="/"
                    className="text-gray-600 hover:text-green-800 transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-gray-600 hover:text-green-800 transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services"
                    className="text-gray-600 hover:text-green-800 transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Services
                  </Link>
                </li>
                <li>
                  <Link
                    to="/offer"
                    className="text-gray-600 hover:text-green-800 transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Offer
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-gray-600 hover:text-green-800 transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </header>
        <main>
          <Outlet />
        </main>
      </div>
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          {/* FAQ Section */}
          <div className="rounded-2xl p-8 mb-12">
            <h3 className="text-3xl font-semibold text-white mb-6">
              Frequently Asked Questions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`${
                    openFaqIndex !== index && "max-h-[76px]"
                  } bg-gray-800 rounded-lg overflow-hidden transition-all duration-300 hover:scale-110 hover:border`}
                >
                  {/* FAQ Question */}
                  <div
                    onClick={() => toggleFaq(index)}
                    className="w-full flex justify-between items-center p-3 focus:outline-none cursor-pointer"
                  >
                    <span className="text-base text-left">{faq.question}</span>
                    <span className="text-xl">
                      {openFaqIndex === index ? (
                        <span className="transform rotate-180">▲</span>
                      ) : (
                        <span>▼</span>
                      )}
                    </span>
                  </div>

                  {/* FAQ Answer */}
                  {openFaqIndex === index && (
                    <div className="px-6 pb-6 pt-2 bg-gray-700 max-h-40 overflow-y-auto">
                      <p className="text-gray-300">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer Content */}
          <div className="text-center border-t border-gray-800 pt-8">
            <p className="text-gray-400">
              © 2025 Modular Homes. All rights reserved.
            </p>
            <p className="text-gray-400 mt-2">Designed by BB Code</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App
